'use client';

import classNames from 'classnames';
import { ChevronDown } from 'lucide-react';
import dynamic from 'next/dynamic';
import { useCallback, useEffect, useState } from 'react';
import { usePrevious } from 'react-use';
import { useDebouncedCallback } from 'use-debounce';

import { Link } from '@/components/Link/Link';
import { useRouteChange } from '@/hooks/useRouteChange';

import type { Dropdown } from '../Dropdown/types';

import { CLOSE_MENU_DELAY, getAnimationDirection } from './transitionsConfig';

import styles from './DesktopDropdownMenu.module.scss';

const DropdownItem = dynamic(() => import('./DropdownItem').then((mod) => mod.DropdownItem), {
    ssr: false,
});

export interface Props {
    dropdowns: Dropdown[];
}

function DesktopDropdownMenu({ dropdowns }: Props) {
    const [activeDropdownId, setActiveDropdownId] = useState<Dropdown['id']>();
    const [activeMenuItem, setActiveMenuItem] = useState<HTMLButtonElement>();
    const [dropdownArrowPosition, setDropdownArrowPosition] = useState<number>();
    const [isKeyboardNavigationEnabled, setIsKeyboardNavigationEnabled] = useState(false);
    const activeDropdown = dropdowns.find((dropdown) => dropdown.id === activeDropdownId);
    const previousDropdownId = usePrevious(activeDropdownId);

    const animationDirection = getAnimationDirection(
        dropdowns,
        activeDropdownId,
        previousDropdownId,
    );

    useEffect(() => {
        if (!activeMenuItem) {
            setDropdownArrowPosition(0);
            return;
        }

        const menuElementRect = activeMenuItem.getBoundingClientRect();
        const menuElementParentRect = activeMenuItem.parentElement!.getBoundingClientRect();

        setDropdownArrowPosition(
            menuElementRect.x - menuElementParentRect.x + menuElementRect.width / 2,
        );
    }, [activeMenuItem]);

    const closeMenu = useCallback(() => {
        setActiveDropdownId(undefined);
        setActiveMenuItem(undefined);
        setIsKeyboardNavigationEnabled(false);
    }, []);

    const closeMenuDebounced = useDebouncedCallback(closeMenu, CLOSE_MENU_DELAY);
    const closeMenuKeyboardDebounced = useDebouncedCallback(closeMenu, 0); // We need this to prevent menu from closing when another menu item is focused right after onBlur

    const preventMenuClosing = () => {
        closeMenuDebounced.cancel();
        closeMenuKeyboardDebounced.cancel();
    };

    const openMenu = (dropdownId: Dropdown['id']) => {
        preventMenuClosing();
        setActiveDropdownId(dropdownId);
    };

    useRouteChange(() => closeMenu());

    return (
        <div className={styles.menuWrapper}>
            <div className={styles.menu}>
                {dropdowns.map((dropdown) => (
                    <button
                        className={classNames(styles.item)}
                        key={dropdown.id}
                        onBlur={() => {
                            if (isKeyboardNavigationEnabled) {
                                closeMenuKeyboardDebounced();
                            }
                        }}
                        onFocus={(event) => {
                            setIsKeyboardNavigationEnabled(true);
                            setActiveMenuItem(event.currentTarget);
                            openMenu(dropdown.id);
                        }}
                        onMouseLeave={closeMenuDebounced}
                        onMouseEnter={(event) => {
                            setIsKeyboardNavigationEnabled(false);
                            setActiveMenuItem(event.currentTarget);
                            openMenu(dropdown.id);
                        }}
                        title={dropdown.id}
                        onMouseDown={(event) => event.preventDefault()}
                    >
                        {dropdown.id}
                        <ChevronDown
                            className={classNames(styles.caretIcon, {
                                [styles.active]: activeDropdownId === dropdown.id,
                            })}
                        />
                    </button>
                ))}
                <Link href="/pricing" className={styles.item} title="Pricing">
                    Pricing
                </Link>
            </div>

            {activeDropdown && (
                <DropdownItem
                    activeDropdownId={activeDropdownId}
                    activeDropdown={activeDropdown}
                    animationDirection={animationDirection}
                    dropdownArrowPosition={previousDropdownId ? dropdownArrowPosition : undefined}
                    isKeyboardNavigationEnabled={isKeyboardNavigationEnabled}
                    onMouseEnter={preventMenuClosing}
                    onMouseLeave={() => {
                        if (!isKeyboardNavigationEnabled) {
                            closeMenuDebounced();
                        }
                    }}
                    onContentMouseEnter={() => setIsKeyboardNavigationEnabled(false)}
                />
            )}
        </div>
    );
}

export default DesktopDropdownMenu;
